import type { IsoTimestamp } from './common.js';
import type { LeadOutcome } from './leadOutcome.js';
import { botNameAtStart, botNameOf } from '../lib/botName.js';

// THE LEAD RECORD (Speed to Lead Stage 4.5). A lead is (club, E.164 phone). `leads/{accountId}__{phoneHash}`: the doc id
// carries a HASH of the phone (the same hash as Do-not-contact), never the digits. The phone is a field, as elsewhere.
//
// ⚠️ Five layers, each with its own writer: the profile (AI), the service badges and the contact status (automatic,
// never hand-edited), the outcome (an AI/system suggestion moves it forward, a person can set anything) and the
// "needs a person" flag. See src/lib/leads/*.

export type LeadQuality = 'hot' | 'warm' | 'cold' | 'unknown';
export const LEAD_QUALITIES: LeadQuality[] = ['hot', 'warm', 'cold', 'unknown'];
export const LEAD_QUALITY_LABELS: Record<LeadQuality, string> = { hot: 'Hot', warm: 'Warm', cold: 'Cold', unknown: 'Unknown' };

export interface LeadProfile {
  goal: string;
  /** Shown as "Why now". */
  motivation: string;
  /** Shown as "Times that suit". */
  preferredTimes: string;
  objections: string;
  quality: LeadQuality;
  updatedAt: IsoTimestamp | null;
  /** Where the last change came from: Sam's reply call, or a call summary (with its id). Ids only. */
  source: { kind: 'sam' | 'call'; ref: string } | null;
}

export type ServiceFlag = 'samTexted' | 'leadRepliedToSam' | 's2lCallMade' | 's2lConnected' | 'leadCalledIn';
export const SERVICE_FLAGS: ServiceFlag[] = ['samTexted', 'leadRepliedToSam', 's2lCallMade', 's2lConnected', 'leadCalledIn'];
/**
 * The badge label for a service flag. ⚠️ The two SMS badges carry the CLUB's bot name ("Mia texted"), so this is a function
 * and not a table; the stored flag codes (`samTexted`, `leadRepliedToSam`) never change. An empty name reads "the assistant".
 */
export function serviceFlagLabel(flag: ServiceFlag, assistantName: string | null | undefined): string {
  const name = botNameOf(assistantName);
  switch (flag) {
    case 'samTexted': return `${botNameAtStart(assistantName)} texted`;
    case 'leadRepliedToSam': return `Lead replied to ${name}`;
    case 's2lCallMade': return 'S2L call made';
    case 's2lConnected': return 'S2L connected';
    case 'leadCalledIn': return 'Lead called in';
  }
}

/** Each flag is the time the system FIRST observed the fact, or null. Set only by the observing path; never cleared. */
export type LeadServices = Record<ServiceFlag, IsoTimestamp | null>;

/** `contacted` = a text went out and the lead has not engaged yet; `in_conversation` = they replied or a person reached them on a call. */
export type ContactStatus = 'not_contacted' | 'contacted' | 'in_conversation';
export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = { not_contacted: 'Not yet contacted', contacted: 'Contacted', in_conversation: 'In conversation' };

export interface LeadContact {
  status: ContactStatus;
  at: IsoTimestamp | null;
  /** How the lead was first reached; `both` is not stored here (see LeadServices). */
  via: 'sam' | 'call' | null;
}

export type OutcomeSetBy = 'person' | 'ai' | 'system';

export interface LeadOutcomeRecord {
  code: LeadOutcome;
  /** Null while `unresolved` and nothing has ever touched it. */
  setBy: OutcomeSetBy | null;
  setAt: IsoTimestamp | null;
  /** The person who confirmed it (setBy 'person'). */
  confirmedBy?: string | null;
  confirmedByName?: string | null;
  /** The call or conversation the suggestion came from. Ids only. */
  sourceRef?: { kind: 'call' | 'conversation'; id: string } | null;
}

export interface NeedsPerson {
  on: boolean;
  setBy: 'person' | 'system' | null;
  /** A code ('handoff', ...), never prose. */
  reason: string | null;
  at: IsoTimestamp | null;
}

export interface Lead {
  id: string;
  accountId: string;
  /** E.164. Masked in every log line. */
  phone: string;
  name: string | null;
  source: string | null;
  /** The enquiry's email, when the enquiry carried one (set from the conversation at creation; absent on older leads). Searchable; shown to staff and the club. */
  email?: string | null;
  /** The ad campaign the enquiry came from (same rule as `email`). */
  campaign?: string | null;
  isTest: boolean;
  /**
   * HISTORIC (leads:backfill-history): built from a relay submission that arrived BEFORE the intake outbox existed, so nothing here ever
   * contacted this person. ⚠️ It counts only as an enquiry and appears under "All" with "Sent to your CRM". Excluded from speed to lead,
   * reached %, staff answer rate, "Not contacted", "Needs you" and every call or SMS metric (the rules read this flag). Absent on every live lead.
   */
  historic?: boolean;
  /** When the enquiry arrived (intake) or the lead first appeared. */
  receivedAt: IsoTimestamp;
  firstSeenAt: IsoTimestamp;
  lastActivityAt: IsoTimestamp;
  /** The earliest time the system acted towards this lead (a Sam text or the lead being dialled). */
  firstResponseAt: IsoTimestamp | null;
  firstResponseVia: 'sam' | 'call' | null;
  /**
   * When the ENQUIRY that earned the first response arrived (the intake event's receipt time), stored beside `firstResponseAt` so the
   * speed is measured against the right enquiry. Absent on older leads (see `responseSpeedSeconds`).
   */
  firstResponseEnquiryAt?: IsoTimestamp | null;
  profile: LeadProfile;
  services: LeadServices;
  /** An inbound call from this lead that a person ACCEPTED (counts as being in conversation). */
  inboundAnsweredAt: IsoTimestamp | null;
  contact: LeadContact;
  outcome: LeadOutcomeRecord;
  needsPerson: NeedsPerson;
  /**
   * When possible distress was first flagged on any of this lead's conversations (a time, never text). Stamped best-effort by the
   * distress path and idempotent (the EARLIEST time wins, so a replay never moves it). Absent = never flagged (and on older docs).
   * ⚠️ It is a SORT and a MARK, not a state: a distressed lead floats to the top of the list only while it still needs a person.
   */
  distressAt?: IsoTimestamp | null;

  /**
   * DEMO DATA (KyckConnect demo seed): the run id on a seeded, entirely synthetic doc. ⚠️ Per DOC, never per account: every outbound path
   * refuses a doc that carries it (src/lib/demoData/demoGuard.ts). Absent on every real doc.
   */
  demoSeed?: string;  /** Ids, so the counts are idempotent: a replayed event never adds one. Capped. */
  conversationIds: string[];
  /** `outbound:<id>` / `inbound:<id>`. */
  callIds: string[];
  latestConversationId: string | null;
  latestCallRef: { kind: 'outbound' | 'inbound'; id: string } | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  /** A real Timestamp for a Firestore TTL policy: 24 months after lastActivityAt. NEVER shortened by an older write. */
  expiresAt: Date;
}

/** Leads expire this long after their last activity. */
export const LEAD_RETENTION_DAYS = 730;
export const LEAD_ID_LIST_CAP = 50;
